import React, { useState, useEffect } from 'react';
import EmailSidebar from './EmailSidebar';
import EmailList from './EmailList';
import EmailView from './EmailView';
import EmailCompose from './EmailCompose';
import EmailToolbar from './EmailToolbar';
import './Email.css';

const EmailPage = () => {
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [emails, setEmails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get user info from localStorage
  const userEmail = localStorage.getItem('userEmail');
  const userName = localStorage.getItem('userName');

  // API base URL based on environment
  const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';
  useEffect(() => {
    fetchEmails();
  }, [selectedFolder, refreshKey]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/emails/${selectedFolder}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle both paginated response and direct array
        const emailList = data.emails || data || [];
        // Ensure it's always an array
        setEmails(Array.isArray(emailList) ? emailList : []);
      } else {
        console.error('Failed to fetch emails:', response.status);
        setEmails([]);
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
      setEmails([]);
    }
    setLoading(false);
  };

  const handleFolderChange = (folder) => {
    setSelectedFolder(folder);
    setSelectedEmail(null);
    setIsComposing(false);
  };

  const handleEmailSelect = (email) => {
    setSelectedEmail(email);
    setIsComposing(false);
    
    // Mark as read
    if (!email.read && selectedFolder === 'inbox') {
      markAsRead(email._id);
    }
  };

  const markAsRead = async (emailId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/emails/${emailId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Update local state
      setEmails(prevEmails => 
        Array.isArray(prevEmails) ? prevEmails.map(email => 
          email._id === emailId ? { ...email, read: true } : email
        ) : []
      );
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  };

  const handleCompose = () => {
    setIsComposing(true);
    setSelectedEmail(null);
  };

  const handleSendEmail = async (emailData, isFormData = false) => {
    try {
      const token = localStorage.getItem('token');
      
      let response;
      
      if (isFormData) {
        // Handle emails with attachments using FormData
        response = await fetch(`${API_BASE_URL}/api/emails/send-with-attachments`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type for FormData - browser will set it with boundary
          },
          body: emailData // emailData is FormData object
        });
      } else {
        // Handle simple emails without attachments using JSON
        response = await fetch(`${API_BASE_URL}/api/emails/send`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            to: emailData.to,
            subject: emailData.subject,
            body: emailData.body,
            cc: emailData.cc || '',
            bcc: emailData.bcc || '',
            fromEmail: emailData.fromEmail
          })
        });
      }
      
      if (response.ok) {
        setIsComposing(false);
        setRefreshKey(prev => prev + 1);
        alert('Email enviado com sucesso!');
      } else {
        throw new Error('Falha ao enviar email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Erro ao enviar email: ' + error.message);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Tem certeza que deseja excluir este email?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/emails/${emailId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setEmails(prevEmails => 
          Array.isArray(prevEmails) ? prevEmails.filter(email => email._id !== emailId) : []
        );
        if (selectedEmail?._id === emailId) {
          setSelectedEmail(null);
        }
      }
    } catch (error) {
      console.error('Error deleting email:', error);
    }
  };

  const filteredEmails = Array.isArray(emails) ? emails.filter(email => {
    const searchLower = searchTerm.toLowerCase();
    return (
      email.subject?.toLowerCase().includes(searchLower) ||
      email.from?.toLowerCase().includes(searchLower) ||
      email.body?.toLowerCase().includes(searchLower)
    );
  }) : [];

  return (
    <div className="email-container">
      <EmailSidebar 
        selectedFolder={selectedFolder}
        onFolderChange={handleFolderChange}
        onCompose={handleCompose}
        emailCounts={{
          inbox: Array.isArray(emails) ? emails.filter(e => !e.read).length : 0,
          total: Array.isArray(emails) ? emails.length : 0
        }}
      />
      
      <div className="email-main">
        <EmailToolbar 
          onSearch={setSearchTerm}
          searchTerm={searchTerm}
          onRefresh={() => setRefreshKey(prev => prev + 1)}
          selectedEmail={selectedEmail}
          onDelete={() => selectedEmail && handleDeleteEmail(selectedEmail._id)}
        />
        
        <div className="email-content">
          {!isComposing && !selectedEmail && (
            <EmailList 
              emails={filteredEmails}
              selectedEmail={selectedEmail}
              onEmailSelect={handleEmailSelect}
              loading={loading}
              folder={selectedFolder}
            />
          )}
          
          {selectedEmail && !isComposing && (
            <EmailView 
              email={selectedEmail}
              onReply={() => {
                setIsComposing(true);
                // Keep selectedEmail so it can be used as replyTo
              }}
              onForward={() => {
                setIsComposing(true);
                // Keep selectedEmail so it can be used for forwarding
              }}
              onDelete={() => handleDeleteEmail(selectedEmail._id)}
            />
          )}
          
          {isComposing && (
            <EmailCompose 
              onSend={handleSendEmail}
              onCancel={() => setIsComposing(false)}
              userEmail={userEmail}
              userName={userName}
              replyTo={selectedEmail}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailPage;